import { ColumnDef, getCoreRowModel, Row, Table, useReactTable } from '@tanstack/react-table'
// import { useVirtualizer } from '@tanstack/react-virtual'
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'

import { Button, TableShimmer } from '@ui/components/core'
import { AddAttributeColumns } from '@ui/components/elements/AddAttributeColumns'
import { RefreshIcon } from '@ui/components/icons'
import { AttributeKey, DatableLog, DatableTrace, KeyValue, SimulateChain, TelemetryEvent } from '@ui/interfaces'
import { extractUniqueKeys, parseCellValue } from '@ui/lib/helpers/tableHelpers'
import { useCodeNodeStore } from '@ui/stores'
import * as Diff from 'diff'

import { DBLBody } from './DBLBody'
import { DBLHeaders } from './DBLHeaders'
import { DetailsModal } from './DetailsModal'

interface FetchMoreProps {
  isFetchingMore: boolean
  onFetchMore: () => void
}

interface TableProps {
  dataSource: 'logs' | 'spans' | 'metrics' | string
  tableData: TelemetryEvent<DatableLog | DatableTrace>[] | SimulateChain[] | Array<object>
  defaultColumnOrder: string[]
  tableType?: 'input' | 'panelInput' | 'output' | 'data'
  loading?: boolean
  customTableHeader?: ReactNode
  customTableHeaderButton?: ReactNode
  showAddColumns?: boolean
  tableRowClick?: ({ row }: { row: Row<KeyValue> }) => void
  fetchMoreProps?: FetchMoreProps
  hideHeaders?: boolean
  condensedStyle?: boolean
  uniqueKeys?: AttributeKey[]
  customColumnWidth?: number
  detailsModal?: boolean
}

type KeyValuedRow = Row<KeyValue> & KeyValue

const RowShimmer = ({ table }: { table: Table<KeyValue> }) => {
  return (
    <div
      className="flex animate-pulse cursor-pointer flex-row border-b hover:border-primary-500 hover:bg-primary-100"
      style={{ width: table.getTotalSize() }}>
      {table.getFlatHeaders().map((header) => {
        return (
          <div
            key={header.id}
            className="h-10 truncate rounded border-r bg-gray-300"
            style={{ width: header.getSize() }}
          />
        )
      })}
    </div>
  )
}

/**
 * DBLTable - A versatile table component for displaying and comparing logs, spans, metrics, and custom data.
 *
 * This component is commonly used for analyzing pipeline node data by displaying logs, spans, metrics, and other data types
 * with options for custom headers, column ordering, and row click actions.
 */

export const DBLTable = ({
  dataSource,
  tableData,
  tableType = 'data',
  loading = false,
  defaultColumnOrder,
  customTableHeader,
  customTableHeaderButton,
  showAddColumns = true,
  tableRowClick,
  fetchMoreProps,
  condensedStyle,
  hideHeaders,
  uniqueKeys: passedUniqueKeys,
  customColumnWidth,
  detailsModal = false,
}: TableProps) => {
  const tableContainerRef = useRef<HTMLDivElement>(null)
  const settingHeadersRef = useRef<boolean>(false)

  const TIMESTAMP_WIDTH = 200
  const CONDENSED_TIMESTAMP_WIDTH = 170
  const DEFAULT_WIDTH = 300

  const { setDiffIndex, setEventViewIndex } = useCodeNodeStore(
    useShallow((state) => ({
      setDiffIndex: state.setDiffIndex,
      setEventViewIndex: state.setEventViewIndex,
    }))
  )

  const { uniqueKeys, addedKeys, removedKeys } = useMemo(() => {
    // Prioritize passedUniqueKeys over tableData
    if (passedUniqueKeys && passedUniqueKeys.length > 0) {
      return { uniqueKeys: passedUniqueKeys, addedKeys: [], removedKeys: [] }
    }

    // if we don't have tableData, we can't extract unique keys
    if (!tableData) return { uniqueKeys: [], addedKeys: [], removedKeys: [] }

    // Input functions similar to flat table data
    if (tableType !== 'output') {
      const extractedKeys = extractUniqueKeys(tableData, tableType)
      return { uniqueKeys: extractedKeys, addedKeys: [], removedKeys: [] }
    } else {
      // Output functions similar to nested table data
      const outputKeys = new Set(extractUniqueKeys(tableData, 'output'))
      const inputKeys = new Set(extractUniqueKeys(tableData, 'input'))

      const addedKeys = [...outputKeys].filter((key) => !inputKeys.has(key))
      const removedKeys = [...inputKeys].filter((key) => !outputKeys.has(key))
      const unchangedKeys = [...inputKeys].filter((key) => outputKeys.has(key))

      const uniqueKeys = [...addedKeys, ...removedKeys, ...unchangedKeys] as AttributeKey[]

      return { uniqueKeys, addedKeys, removedKeys }
    }
  }, [tableData, tableType, passedUniqueKeys])

  const [headersSet, setHeadersSet] = useState(false)
  const [widthSet, setWidthSet] = useState(false)

  const [columnWidth, setColumnWidth] = useState<number>(0)
  const [columnVisibility, setColumnVisibility] = useState<{ [key: string]: boolean }>({})
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [modalRow, setModalRow] = useState<Row<KeyValue> | null>(null)
  const [activeRow, setActiveRow] = useState<number | null>(null)

  // Default column order for logs and spans
  // Not working as expected so defaultColumnOrder is required
  // const defaultColumnOrderOptions: DefaultColumnOrder = {
  //   logs: ['expander', 'timestamp', 'resource.service.name', 'resource.host.name', 'body'],
  //   spans: ['expander', 'timestamp', 'resource.service.name', 'resource.host.name', 'name'],
  //   metrics: ['expander', 'timestamp', 'name', 'attributes'],
  //   custom: customColumnOrder ?? [],
  // }

  // const defaultColumnOrder: string[] = defaultColumnOrderOptions[dataSource] ?? defaultColumnOrderOptions.custom

  const columns: ColumnDef<KeyValue>[] = useMemo(() => {
    const dataHeaders = uniqueKeys.map((key: AttributeKey) => {
      const keys = key.key ?? key
      const columnSizeMap: Record<string, number> = {
        timestamp: !condensedStyle ? TIMESTAMP_WIDTH : CONDENSED_TIMESTAMP_WIDTH,
        default: customColumnWidth || DEFAULT_WIDTH,
      }
      return {
        id: keys,
        accessorFn: (row: KeyValuedRow) => {
          if (row && row.record) {
            const cellValue = parseCellValue({ key: keys, obj: row, isTelemetryEvent: true })

            return {
              val: cellValue,
              diff: [],
            }
          } else if (row && 'input' in row && 'output' in row) {
            const inputValue = parseCellValue({ key: keys, obj: row.input, isTelemetryEvent: false })
            const outputValue = parseCellValue({ key: keys, obj: row.output, isTelemetryEvent: false })

            const diff = Diff.diffJson(inputValue, outputValue)
            const panelInputDiff = Diff.diffJson(inputValue, inputValue)

            switch (tableType) {
              case 'input':
                return { val: inputValue, diff }
              case 'output':
                return { val: outputValue, diff }
              case 'panelInput':
                return { val: inputValue, diff: panelInputDiff }
            }

            return
          } else {
            const cellValue = parseCellValue({ key: keys, obj: row, isTelemetryEvent: false })

            return {
              val: cellValue,
              diff: [],
            }
          }
        },
        header: keys,
        size: columnWidth,
        minSize: columnSizeMap[keys] || columnSizeMap.default,
      }
    })

    const expander = [
      {
        header: '',
        id: 'expander', // It needs an ID
        enableResizing: false,
        maxSize: 40,
        accessorFn: () => {
          return { val: '', diff: [] }
        },
      },
    ]

    const columns =
      tableType === 'output' || tableType === 'panelInput' ? [...expander, ...dataHeaders] : [...dataHeaders]

    return columns
  }, [uniqueKeys, tableType, columnWidth, condensedStyle, customColumnWidth])

  const table = useReactTable({
    data: tableData ?? [],
    columns: columns as ColumnDef<object>[],
    state: {
      columnOrder: ['expander', 'timestamp', ...defaultColumnOrder],
      columnVisibility: columnVisibility,
    },
    columnResizeMode: 'onChange',
    // defaultColumn: {
    //   minSize: 200,
    //   size: columnWidth,
    // },
    getCoreRowModel: getCoreRowModel(),
    getRowCanExpand: () => true,
    onColumnVisibilityChange: setColumnVisibility,
  })

  useEffect(() => {
    if (!tableData || !dataSource) return

    settingHeadersRef.current = true

    const alwaysVisibleColumns = ['expander', 'timestamp']
    const updatedColumnOrder = [...alwaysVisibleColumns, ...defaultColumnOrder]

    const isKeyVisible = (key: AttributeKey) => {
      const keyString = typeof key === 'string' ? key : key.key

      return updatedColumnOrder.includes(keyString) || addedKeys.includes(key) || removedKeys.includes(key)
    }

    const newColumnVisibility = uniqueKeys.reduce(
      (acc, key) => {
        const keyString = typeof key === 'string' ? key : key.key
        acc[keyString] = isKeyVisible(key)
        return acc
      },
      {} as Record<string, boolean>
    )

    setColumnVisibility(newColumnVisibility)
    settingHeadersRef.current = false
    setHeadersSet(true)
  }, [tableData, dataSource, defaultColumnOrder, uniqueKeys, addedKeys, removedKeys, condensedStyle])

  // For auto resizing columns
  const visibleColumnsAmount = useMemo(() => {
    return Object.keys(columnVisibility).filter((key) => columnVisibility[key]).length
  }, [columnVisibility])

  useEffect(() => {
    if (headersSet && tableContainerRef.current && !settingHeadersRef.current) {
      // If the expander column exists we need to subtract 40px from the total width
      const doesExpanderColumnExist = columns.find((column) => column.id === 'expander')

      if (customColumnWidth) {
        setColumnWidth(customColumnWidth - (doesExpanderColumnExist ? 40 : 0))
      } else {
        // We need to make up the diference in width because timestamp is always a
        // fixed width of 200px so we need to add the difference to the other columns

        setColumnWidth(
          (tableContainerRef.current.clientWidth - TIMESTAMP_WIDTH - (doesExpanderColumnExist ? 40 : 0)) /
            Math.min(visibleColumnsAmount - (doesExpanderColumnExist ? 1 : 0), 8)
        )
      }
      setWidthSet(true)
    }
  }, [columns, customColumnWidth, headersSet, visibleColumnsAmount, widthSet])

  const { rows } = table.getRowModel()

  const handleRowClick = ({ row }: { row: Row<KeyValue> }) => {
    setShowDetailsModal(true)
    setModalRow(row)
    setActiveRow(row.index)
  }

  const loadRowIntoDetailsModal = (index: number) => {
    setModalRow(rows[index])
    setActiveRow(index)
  }

  const hideAllColumns = () => {
    // hide all columns except the expander and timestamp
    for (const column of table.getAllLeafColumns()) {
      if (column.id !== 'expander' && column.id !== 'timestamp') {
        setColumnVisibility((prev) => ({ ...prev, [column.id]: false }))
      }
    }
  }

  const showAllColumns = () => {
    table.toggleAllColumnsVisible(true)
  }

  const showLoading = loading || !headersSet || !widthSet
  const hasNoData = tableData && tableData.length === 0 && !loading && headersSet && widthSet

  return (
    <>
      <div className={'relative flex h-full w-full flex-col rounded-sm border-x border-t border-gray-200 bg-white'}>
        {hasNoData && (
          <div className="absolute inset-0 z-20 mt-14 flex items-center justify-center bg-white/90 backdrop-blur-sm">
            <p className="text-lg font-semibold">No data to display</p>
          </div>
        )}

        {!hideHeaders && (
          <div className="flex w-full justify-between p-2.5">
            {customTableHeader ? (
              <div className="flex w-full items-center justify-between">
                <div className="flex flex-row items-center space-x-2.5">{customTableHeader}</div>
                <div className="flex flex-row items-center space-x-2.5">
                  <>
                    {fetchMoreProps && <Button onClick={() => fetchMoreProps.onFetchMore()}>Fetch More</Button>}

                    {showAddColumns && (
                      <AddAttributeColumns
                        columns={table.getAllLeafColumns()}
                        hideAllColumns={hideAllColumns}
                        showAllColumns={showAllColumns}
                      />
                    )}
                  </>

                  {customTableHeaderButton && (
                    <div className="flex flex-row items-center space-x-2.5">{customTableHeaderButton}</div>
                  )}
                </div>
              </div>
            ) : (
              <>
                <p className="my-auto text-sm font-medium">
                  Displaying {tableData.length} {dataSource}
                </p>

                <div className="flex items-center space-x-2.5">
                  {fetchMoreProps && (
                    <Button
                      size="small"
                      variant="tertiary"
                      icon={<RefreshIcon className="size-4 fill-none stroke-gray-900" />}
                      iconPosition="left"
                      disabled={fetchMoreProps.isFetchingMore}
                      onClick={() => fetchMoreProps.onFetchMore()}>
                      Fetch more data
                    </Button>
                  )}

                  <AddAttributeColumns
                    columns={table.getAllLeafColumns()}
                    hideAllColumns={hideAllColumns}
                    showAllColumns={showAllColumns}
                  />

                  {customTableHeaderButton && (
                    <div className="flex flex-row items-center space-x-2.5">{customTableHeaderButton}</div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        <div
          className="flex h-full w-full flex-col overflow-scroll rounded-sm pr-4 ring-1 ring-gray-200"
          ref={tableContainerRef}
          data-testid="telemetry-table">
          {showLoading ? (
            <TableShimmer />
          ) : (
            <div
              className="table w-full divide-y divide-gray-200 bg-white first-of-type:divide-y-0"
              style={{ width: table.getTotalSize() }}>
              {!hideHeaders && <DBLHeaders headers={table.getHeaderGroups()} dataSource={dataSource} />}
              <DBLBody
                rows={rows}
                dataSource={dataSource}
                tableType={tableType}
                setDiffIndex={setDiffIndex}
                setEventViewIndex={setEventViewIndex}
                handleRowClick={tableRowClick ?? handleRowClick}
                activeRow={activeRow}
                condensedStyle={condensedStyle}
              />
            </div>
          )}
          {fetchMoreProps && fetchMoreProps.isFetchingMore && <RowShimmer table={table} />}
        </div>
      </div>

      {detailsModal && (
        <DetailsModal
          dataSource={dataSource}
          show={showDetailsModal}
          row={modalRow}
          rowsLength={rows.length}
          closeModal={() => setShowDetailsModal(false)}
          setModalRow={loadRowIntoDetailsModal}
          setHighlightedRow={setActiveRow}
        />
      )}
    </>
  )
}
